<template>
  <div class="dashboard-resources-list">
    <h3 class="dashboard-h3">{{ $t("components.dashboard.resources_list.title") }}</h3>

    <ul class="dashboard-resources-list__list">
      <li v-if="startingGuideUrls.length > 0" class="dashboard-resources-list__item">
        <div class="dashboard-resources-list__item-header">
          <img class="dashboard-resources-list__item-image" src="/icons/Guide-depart_vecteur.svg" />

          <div class="dashboard-resources-list__item-content">
            <a
              v-if="approvedCommunities.length === 1"
              :href="startingGuideUrls[0].url"
              target="_blank"
            >
              {{ $tc("components.dashboard.resources_list.starting_guide", 1) }}
            </a>
            <template v-else>
              {{ $tc("components.dashboard.resources_list.starting_guide", 2) }}
            </template>

            <div v-if="approvedCommunities.length > 1" class="dashboard-resources-list__item-list">
              <ul>
                <li v-for="c in startingGuideUrls" :key="c.url">
                  <a :href="c.url" target="_blank">{{ c.community }}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </li>

      <li v-if="communitiesWithContact.length > 0" class="dashboard-resources-list__item">
        <div class="dashboard-resources-list__item-header">
          <img class="dashboard-resources-list__item-image" src="/icons/Email.png" />

          <div class="dashboard-resources-list__item-content">
            <!-- If approved in more than one community, then display bullet lists for chat groups and starting guides -->
            <a
              v-if="approvedCommunities.length == 1"
              :href="`mailto:${communitiesWithContact[0].contact_email}`"
              target="_blank"
            >
              {{ $t("components.dashboard.resources_list.contact_community") }}
            </a>
            <template v-else>
              {{ $t("components.dashboard.resources_list.contact_community") }}
            </template>
            <div v-if="approvedCommunities.length > 1" class="dashboard-resources-list__item-list">
              <ul>
                <li v-for="c in communitiesWithContact" :key="c.id">
                  <a :href="`mailto:${c.contact_email}`" target="_blank">{{ c.name }}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </li>

      <li v-if="communitiesWithChatGroup.length > 0" class="dashboard-resources-list__item">
        <div class="dashboard-resources-list__item-header">
          <img class="dashboard-resources-list__item-image" src="/icons/messenger.png" />

          <div class="dashboard-resources-list__item-content">
            <!-- If approved in more than one community, then display bullet lists for chat groups and starting guides -->
            <a
              v-if="approvedCommunities.length == 1"
              :href="communitiesWithChatGroup[0].chat_group_url"
              target="_blank"
            >
              {{ $t("components.dashboard.resources_list.chat_with_community") }}
            </a>
            <template v-else>
              {{ $t("components.dashboard.resources_list.chat_with_community") }}
            </template>
            <div v-if="approvedCommunities.length > 1" class="dashboard-resources-list__item-list">
              <ul>
                <li v-for="c in communitiesWithChatGroup" :key="c.id">
                  <a :href="c.chat_group_url" target="_blank">{{ c.name }}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </li>

      <li
        v-if="inCarLoaningCommunity"
        class="dashboard-resources-list__item dashboard-resources-list__item-header"
      >
        <img class="dashboard-resources-list__item-image" src="/icons/Assurance_vecteur.svg" />

        <div class="dashboard-resources-list__item-content">
          <div class="dashboard-resources-list__item-list">
            <ul>
              <li>
                <a target="_blank" :href="$t('conditions.insurance_conditions_url')">
                  {{ $t("conditions.insurance_name") | capitalize }}
                </a>
              </li>
              <li>
                <a target="_blank" :href="$t('conditions.insurance_policy_url')">
                  {{ $t("conditions.insurance_policy_name") | capitalize }}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </li>

      <li v-if="inCarLoaningCommunity" class="dashboard-resources-list__item">
        <a
          :href="$t('resources.pricings_url')"
          class="dashboard-resources-list__item-header"
          target="_blank"
        >
          <img class="dashboard-resources-list__item-image" src="/icons/Tarification_vecteur.svg" />

          <div class="dashboard-resources-list__item-content">
            {{ $t("components.dashboard.resources_list.pricing") }}
          </div>
        </a>
      </li>

      <li class="dashboard-resources-list__item">
        <a
          :href="$t('resources.base_url')"
          class="dashboard-resources-list__item-header"
          target="_blank"
        >
          <img class="dashboard-resources-list__item-image" src="/icons/learn_more.svg" />

          <div class="dashboard-resources-list__item-content">
            {{ $t("components.dashboard.resources_list.learn_more") }}
          </div>
        </a>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: "DashboardResourcesList",
  props: {
    user: {
      required: true,
      type: Object,
    },
  },
  computed: {
    approvedCommunities() {
      return (this?.user?.accessible_communities ?? [])
        .filter((c) => c.status === "approved")
        .map((c) => c.community);
    },
    communitiesWithChatGroup() {
      if (!this.user || !this.approvedCommunities) {
        return [];
      }
      return this.approvedCommunities.filter(
        (c) => !!c.chat_group_url && c.chat_group_url.length > 0
      );
    },
    communitiesWithContact() {
      if (!this.user || !this.approvedCommunities) {
        return [];
      }
      return this.approvedCommunities.filter(
        (c) => !!c.contact_email && c.contact_email.length > 0
      );
    },
    inCarLoaningCommunity() {
      return this.user.available_loanable_types.includes("car");
    },
    startingGuideUrls() {
      let communityStartingGuides = [];

      communityStartingGuides = this.approvedCommunities
        .filter((c) => c.starting_guide_url && c.starting_guide_url.length > 0)
        .map((c) => ({ community: c.name, url: c.starting_guide_url }));

      if (communityStartingGuides.length > 0) {
        return communityStartingGuides;
      }

      return [];
    },
  },
};
</script>

<style lang="scss">
.dashboard-resources-list {
  a {
    &,
    &:hover,
    &:active,
    &:focus {
      color: $locomotion-green;
    }
  }

  &__list {
    list-style-type: none;
    padding: 0;
    margin: 0;

    margin-bottom: 2rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__item {
    position: relative;
    margin-top: 1rem;
  }

  &__item-header {
    display: flex;
  }

  &__item-image {
    top: 0;
    left: 0;

    height: 3.5rem;
    width: 3.5rem;
  }

  &__item-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 3.5rem;
    margin-left: 1rem;
  }

  &__item-list {
    & > ul {
      padding-left: 0;
      list-style-type: none;
    }
    & > ul > li {
      line-height: 1.5rem;
    }
  }
}
</style>
